/** Project list with progress and cost variance (§4.7). */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AlertTriangle, Search } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { peso, shortDate } from '../../lib/format';
import {
  Card,
  EmptyState,
  Skeleton,
  StatusChip,
  TableWrap,
  Td,
  Th,
  inputClass,
  type ChipTone,
} from '../../components/ui';

interface ProjectRow {
  id: string;
  code: string;
  name: string;
  status: 'PENDING' | 'ONGOING' | 'COMPLETED' | 'CANCELLED';
  startDate: string | null;
  targetEndDate: string | null;
  pm: { id: string; name: string } | null;
  salesOrder: { id: string; code: string };
  customer: { id: string; name: string };
  progress: { done: number; total: number; pct: number };
  overdueCount: number;
  estimatedCost?: string;
  actualCost?: string;
  costVariance?: string;
  overBudget?: boolean;
}

export const STATUS_TONES: Record<ProjectRow['status'], ChipTone> = {
  PENDING: 'neutral',
  ONGOING: 'info',
  COMPLETED: 'ok',
  CANCELLED: 'neutral',
};

export function ProgressBar({ pct }: { pct: number }): JSX.Element {
  return (
    <div className="flex items-center gap-2">
      <div className="h-2 w-24 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
        <div
          className={`h-full rounded-full ${pct === 100 ? 'bg-emerald-500' : 'bg-brand'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="tabular text-xs text-slate-500">{pct}%</span>
    </div>
  );
}

export function ProjectsPage(): JSX.Element {
  const { can } = useAuth();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');

  const params = new URLSearchParams({ pageSize: '50' });
  if (search) params.set('q', search);
  if (status) params.set('status', status);

  const projects = useQuery({
    queryKey: ['projects', params.toString()],
    queryFn: () => api<{ data: ProjectRow[]; total: number }>(`/projects?${params.toString()}`),
  });

  const showMoney = can('leads.view_financials');

  return (
    <div className="mx-auto max-w-7xl space-y-5">
      <header>
        <h1 className="text-xl font-bold text-navy dark:text-white">Projects</h1>
        <p className="text-sm text-slate-500">
          Delivery against every confirmed contract, with what it is actually costing.
        </p>
      </header>

      <Card>
        <div className="mb-4 flex flex-wrap items-end gap-3">
          <div className="relative min-w-56 flex-1">
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
            />
            <input
              className={`${inputClass} pl-9`}
              placeholder="Search project, code or customer"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
          <select
            className={`${inputClass} w-44`}
            value={status}
            onChange={(event) => setStatus(event.target.value)}
          >
            <option value="">All statuses</option>
            <option value="PENDING">Pending</option>
            <option value="ONGOING">Ongoing</option>
            <option value="COMPLETED">Completed</option>
            <option value="CANCELLED">Cancelled</option>
          </select>
        </div>

        {projects.isLoading && <Skeleton className="h-64 w-full" />}
        {projects.data?.data.length === 0 && (
          <EmptyState
            title="No projects yet"
            description="A project appears as soon as a lead is confirmed."
          />
        )}

        {(projects.data?.data.length ?? 0) > 0 && (
          <TableWrap>
            <thead>
              <tr>
                <Th>Project</Th>
                <Th>Customer</Th>
                <Th>Manager</Th>
                <Th>Progress</Th>
                {showMoney && <Th align="right">Estimated</Th>}
                {showMoney && <Th align="right">Actual</Th>}
                {showMoney && <Th align="right">Variance</Th>}
                <Th>Target end</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {projects.data?.data.map((row) => (
                <tr key={row.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                  <Td>
                    <Link
                      to={`/projects/${row.id}`}
                      className="font-medium text-navy hover:underline dark:text-white"
                    >
                      {row.name}
                    </Link>
                    <p className="font-mono text-xs text-slate-500">
                      {row.code} · {row.salesOrder.code}
                    </p>
                  </Td>
                  <Td>{row.customer.name}</Td>
                  <Td>{row.pm?.name ?? <span className="text-slate-400">Unassigned</span>}</Td>
                  <Td>
                    <ProgressBar pct={row.progress.pct} />
                    <p className="text-xs text-slate-500">
                      {row.progress.done}/{row.progress.total}
                      {row.overdueCount > 0 && (
                        <span className="ml-2 font-semibold text-red-600">
                          {row.overdueCount} overdue
                        </span>
                      )}
                    </p>
                  </Td>
                  {showMoney && (
                    <Td align="right" className="tabular">
                      {peso(row.estimatedCost)}
                    </Td>
                  )}
                  {showMoney && (
                    <Td align="right" className="tabular">
                      {peso(row.actualCost)}
                    </Td>
                  )}
                  {showMoney && (
                    <Td align="right" className="tabular">
                      <span className={row.overBudget ? 'font-semibold text-red-600' : ''}>
                        {row.overBudget && (
                          <AlertTriangle aria-hidden="true" className="mr-1 inline h-3.5 w-3.5" />
                        )}
                        {peso(row.costVariance)}
                      </span>
                    </Td>
                  )}
                  <Td>{shortDate(row.targetEndDate)}</Td>
                  <Td>
                    <StatusChip tone={STATUS_TONES[row.status]}>
                      {row.status.charAt(0) + row.status.slice(1).toLowerCase()}
                    </StatusChip>
                  </Td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        )}
      </Card>
    </div>
  );
}
